/**
 * Australian phone numbers as the reports print them (MB, decision 599, 26 Oct 2026): a number typed any way
 * ("0409966248", "0409-966-248", "+61 409966248") prints grouped as Australians write it:
 *
 * - mobiles (04, 05): "0409 966 248";
 * - landlines with their area code (02, 03, 07, 08): "(08) 9123 4567"; a local 8-digit number: "9123 4567";
 * - 1300 / 1800 numbers: "1300 123 456"; 13 numbers: "13 12 34";
 * - +61 (or 0061), the national number without its leading 0: "+61 409 966 248", "+61 8 9123 4567".
 *
 * Anything else (another country, an extension, a note, a wrong number of digits) is returned exactly as typed.
 * Formatting a formatted number gives the same number. The Rust twin (`teslyn_core::phone::format_au`, for the
 * weekly report's PDF and Word document) follows the same rules and is tested on the same cases.
 */

/** Separators people type inside a number; anything else makes the text "not a phone number". */
const SEPARATORS = /[\s\-.()]/g;

function national(digits: string): string | null {
  if (/^0[45]\d{8}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (/^0[2378]\d{8}$/.test(digits)) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  if (/^1[38]00\d{6}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (/^13\d{4}$/.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
  }
  if (/^[2-9]\d{7}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }
  return null;
}

function international(rest: string): string | null {
  // rest: the national number without its leading 0 (9 digits)
  if (/^[45]\d{8}$/.test(rest)) return `+61 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
  if (/^[2378]\d{8}$/.test(rest)) return `+61 ${rest.slice(0, 1)} ${rest.slice(1, 5)} ${rest.slice(5)}`;
  return null;
}

/** The number grouped the Australian way, or `typed` itself when it is not a number Teslyn recognises. */
export function formatPhoneAu(typed: string): string {
  const trimmed = typed.trim();
  if (trimmed === '') return typed;
  // "+61 (0)409 ...": the (0) people write for callers inside Australia
  const plain = trimmed.replace(/^\+61\s*\(0\)/, '+61').replace(SEPARATORS, '');
  let out: string | null = null;
  if (/^\+61\d+$/.test(plain)) out = international(plain.slice(3).replace(/^0/, ''));
  else if (/^0061\d+$/.test(plain)) out = international(plain.slice(4).replace(/^0/, ''));
  else if (/^\d+$/.test(plain)) out = national(plain);
  return out ?? typed;
}
